import React, { Suspense } from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'
import Layout from './components/Layout/Layout'
import { WithAuthenticationListener } from './components/HOCs/withAuthenticationListener'
import { useAuthentication } from './customHooks/useAuthentication'
import useSyncCurrentUserOnLoad from './customHooks/useSyncCurrentUserOnLoad'
import { getBearerToken } from './utils/sessionAuth'
import ErrorBoundary from './components/ErrorBoundary'
import { Loader } from './components/loader'

import Login from './views/login/Login'
import Signup from './views/login/signup/Signup'
import GoogleReturn from './views/login/staffGoogle/GoogleReturn'
import GoogleFinishRegistration from './views/login/staffGoogle/GoogleFinishRegistration'
import ValidationCode from './views/login/signup/ValidationCode'
import ForgetPassword from './views/login/forget_password/ForgetPassword'
import RecoverAccountPage from './views/Account/pages/RecoverAccountPage'
import AccessDenied from './views/AccessDenied'

import { legacyParamRedirects, legacyStaticRedirects } from './routes/LegacyRedirects'
import { legacyQueryRedirectRules } from './routes/paths'
import LegacyQueryRedirect from './routes/LegacyQueryRedirect'
import paths from './routes/paths'
import { lazyPage } from './routes/lazyPage'
import SuperAdminRoute from './routes/SuperAdminRoute'
import RoleGuardRoute from './routes/RoleGuardRoute'
import ModuleGuardRoute from './routes/ModuleGuardRoute'
import './views/Dashboard/dashboard.css'

const About = lazyPage(() => import('./views/About/About'))
const UserProfile = lazyPage(() => import('./views/user_profile'))
const Categories = lazyPage(() => import('./views/categories'))
const ProductBadgesPage = lazyPage(() => import('./views/productBadges'))
const OnlineSales = lazyPage(() => import('./views/OnlineSales'))
const SaleReturns = lazyPage(() => import('./views/SaleReturns'))
const SaleReturnDetail = lazyPage(() => import('./views/SaleReturns/SaleReturnDetail'))
const RecurringInvoicesList = lazyPage(() => import('./views/RecurringInvoices/index'))
const RecurringInvoiceForm = lazyPage(() => import('./views/RecurringInvoices/RecurringInvoiceForm'))
const RecurringInvoiceDetail = lazyPage(() => import('./views/RecurringInvoices/RecurringInvoiceDetail'))
const Receipt = lazyPage(() => import('./views/Receipt'))
const ItemInventory = lazyPage(() => import('./views/item/ItemInventory').then((mod) => ({ default: WithAuthenticationListener(mod.default) })))
const Dashboard = lazyPage(() => import('./views/Dashboard'))
const InventoryReport = lazyPage(() => import('./views/InventoryReport'))
const SuperAdminCompanies = lazyPage(() => import('./views/Dashboard/components/SuperAdminCompanies'))
const SuperAdminAccountDeletions = lazyPage(() => import('./views/Dashboard/components/SuperAdminAccountDeletions'))
const SuperAdminSalesLeads = lazyPage(() => import('./views/AdminSalesLeads'))
const SuperAdminCompanyUsers = lazyPage(() => import('./views/Dashboard/components/SuperAdminCompanyUsers'))
const SuperAdminActivity = lazyPage(() => import('./views/Dashboard/components/SuperAdminActivity'))
const SuperAdminHealth = lazyPage(() => import('./views/Dashboard/components/SuperAdminHealth'))
const SuperAdminCompanyHealth = lazyPage(() => import('./views/Dashboard/components/SuperAdminCompanyHealth'))
const SuperAdminPlatformUsers = lazyPage(() => import('./views/Dashboard/components/SuperAdminPlatformUsers'))
const SuperAdminPlatformUserForm = lazyPage(() => import('./views/Dashboard/components/SuperAdminPlatformUserForm'))
const AdminPushNotifications = lazyPage(() => import('./views/AdminPushNotifications'))
const SocialContentPrompt = lazyPage(() => import('./views/Admin/SocialContentPrompt'))
const CreativeVisionPrompt = lazyPage(() => import('./views/Admin/CreativeVisionPrompt'))
const NotificationsPage = lazyPage(() => import('./views/Notifications'))
const StorefrontInquiriesPage = lazyPage(() => import('./views/StorefrontInquiries'))
const StorefrontLegalPage = lazyPage(() => import('./views/StorefrontLegal'))
const Settings = lazyPage(() => import('./views/Settings/Settings'))
const SocialMediaAi = lazyPage(() => import('./views/Marketing/SocialMediaAi'))
const CreativeStudio = lazyPage(() => import('./views/Marketing/CreativeStudio'))
const ChartOfAccounts = lazyPage(() => import('./views/Settings/ChartOfAccounts'))
const Subscription = lazyPage(() => import('./views/Subscription'))
const BillingSuccessPage = lazyPage(() => import('./views/Subscription/BillingReturnPages').then((mod) => ({ default: mod.BillingSuccessPage })))
const BillingCancelPage = lazyPage(() => import('./views/Subscription/BillingReturnPages').then((mod) => ({ default: mod.BillingCancelPage })))
const BillingPortalReturnPage = lazyPage(() => import('./views/Subscription/BillingReturnPages').then((mod) => ({ default: mod.BillingPortalReturnPage })))
const PaypalReturnPage = lazyPage(() => import('./views/Subscription/BillingReturnPages').then((mod) => ({ default: mod.PaypalReturnPage })))
const PaypalCancelPage = lazyPage(() => import('./views/Subscription/BillingReturnPages').then((mod) => ({ default: mod.PaypalCancelPage })))
const AccountSettings = lazyPage(() => import('./views/Account'))
const UserManagment = lazyPage(() => import('./views/UserManagment'))
const EditExpenseCategories = lazyPage(() => import('./views/Expensive/exponsiveCategories/editExpenseCategories'))
const ValidationMethodSelection = lazyPage(() => import('./views/login/signup/ValidationMethodSelection'))
const CancelDeletionPage = lazyPage(() => import('./views/Account/pages/CancelDeletionPage'))
const LifecycleReviewPreview = lazyPage(() => import('./views/Account/pages/LifecycleReviewPreview'))
const StockTransfersList = lazyPage(() => import('./views/StockTransfers/index'))
const NewTransfer = lazyPage(() => import('./views/StockTransfers/NewTransfer'))
const TransferDetail = lazyPage(() => import('./views/StockTransfers/TransferDetail'))
const StockReceiptsRoutes = lazyPage(() => import('./views/StockReceipts/StockReceiptsRoutes'))
const PurchaseOrdersRoutes = lazyPage(() => import('./views/PurchaseOrders/PurchaseOrdersRoutes'))
const VendorBillsRoutes = lazyPage(() => import('./views/VendorBills/VendorBillsRoutes'))
const PageNotFound = lazyPage(() => import('./components/pageNotFound/pageNotFound'))
const ProfileForm = lazyPage(() => import('./views/user_profile/updateProfile'))
const POS = lazyPage(() => import('./views/POS'))
const BarcodeGenerator = lazyPage(() => import('./views/BarcodeGenerator'))
const SearchReceipt = lazyPage(() => import('./views/SearchReceipt'))
const SearchInvoice = lazyPage(() => import('./views/SearchInvoice'))
const AccountingOverview = lazyPage(() => import('./views/Accounting/AccountingOverview'))
const ProfitLossReport = lazyPage(() => import('./views/Accounting/ProfitLossReport'))
const AccountsReceivableReport = lazyPage(() => import('./views/Accounting/AccountsReceivableReport'))
const SalesReport = lazyPage(() => import('./views/Accounting/SalesReport'))
const PaymentsReport = lazyPage(() => import('./views/Accounting/PaymentsReport'))
const ExpensesReport = lazyPage(() => import('./views/Accounting/ExpensesReport'))
const WorkOrderPaymentsPage = lazyPage(() => import('./views/WorkOrderPayments'))
const ReceivePaymentsPage = lazyPage(() => import('./views/ReceivePayments'))
const ProductsRoutes = lazyPage(() => import('./views/item/ProductsRoutes'))
const LaborsRoutes = lazyPage(() => import('./views/ProductForm/LaborsRoutes'))
const PackagesRoutes = lazyPage(() => import('./views/package/PackagesRoutes'))
const VendorsRoutes = lazyPage(() => import('./views/Vendor/VendorsRoutes'))
const CustomersRoutes = lazyPage(() => import('./views/costumer/CustomersRoutes'))
const WarehousesRoutes = lazyPage(() => import('./views/WareHouse/WarehousesRoutes'))
const ExpensesRoutes = lazyPage(() => import('./views/Expensive/ExpensesRoutes'))
const AdjustmentsRoutes = lazyPage(() => import('./views/Adjustment/AdjustmentsRoutes'))
const InvoicesRoutes = lazyPage(() => import('./views/Invoice/InvoicesRoutes'))
const EstimatesRoutes = lazyPage(() => import('./views/InvoiceForm/EstimatesRoutes'))
const WorkOrdersRoutes = lazyPage(() => import('./views/Invoice/WorkOrdersRoutes'))
const PaymentsRoutes = lazyPage(() => import('./views/InvoiceForm/PaymentsRoutes'))
const CompaniesRoutes = lazyPage(() => import('./views/companies/CompaniesRoutes'))
const UsersRoutes = lazyPage(() => import('./views/UserManagment/UsersRoutes'))
const DevicesRoutes = lazyPage(() => import('./views/Devices/DevicesRoutes'))
const PayTerms = lazyPage(() => import('./views/PayTerms'))
const WorkOrderTypes = lazyPage(() => import('./views/WorkOrderTypes'))
const Taxes = lazyPage(() => import('./views/Taxes'))
const LegacyCatalogRoutes = lazyPage(() => import('./views/LegacyCatalog/LegacyCatalogRoutes'))

const LEGACY_QUERY_MODERN_PATHS = new Set(['/payments/new', '/receipt-payments/new'])
const LEGACY_CATALOG_PATHS = [paths.paymentAccounts.list, paths.deviceTypes.list, paths.paymentTypes.list, paths.brands.list, paths.productTags.list, paths.attributeDefinitions.list, paths.locations.list, paths.uoms.list, paths.expenseCategories.list]
const LayoutWithAuthenticaction = WithAuthenticationListener(Layout)

const PrivateRoute = ({ children, currentToken, wrapper: Wrapper = LayoutWithAuthenticaction, ...rest }) => {
  return <Route {...rest} render={() => (currentToken ? <Wrapper>{children}</Wrapper> : <Redirect to="/" />)} />
}

const Routes = () => {
  const { currentToken } = useAuthentication()
  const isAuthenticated = Boolean(currentToken || getBearerToken())
  useSyncCurrentUserOnLoad(isAuthenticated)
  return (
    <Suspense fallback={<Loader variant="page" />}>
      <Switch>
        <Route exact path={'/'}>
          <Login />
        </Route>
        <Route exact path={'/check_information/:code?'}>
          <ErrorBoundary>
            <SearchReceipt />
          </ErrorBoundary>
        </Route>
        <PrivateRoute exact path={'/dashboard'} currentToken={isAuthenticated}>
          <Dashboard />
        </PrivateRoute>
        <PrivateRoute exact path={'/inventory-report'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="reports">
            <InventoryReport />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={paths.notifications} currentToken={isAuthenticated}>
          <NotificationsPage />
        </PrivateRoute>
        <PrivateRoute path={'/pos'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="pos">
            <POS />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/barcode-generator'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="products">
            <BarcodeGenerator />
          </ModuleGuardRoute>
        </PrivateRoute>
        <Redirect exact from="/cash-counters" to={paths.pos} />
        <Redirect exact from="/cash-counter" to={paths.pos} />
        <Redirect from="/cash-counter/:id" to={paths.pos} />
        <PrivateRoute exact path={'/about'} currentToken={isAuthenticated} wrapper={React.Fragment}>
          <About />
        </PrivateRoute>
        {legacyStaticRedirects.map(({ from, to }) => (
          <Redirect key={from} exact from={from} to={to} />
        ))}
        {legacyQueryRedirectRules
          .filter((rule) => !LEGACY_QUERY_MODERN_PATHS.has(rule.pathname))
          .map((rule) => (
            <Route key={`${rule.pathname}-${rule.queryKey}`} exact path={rule.pathname} render={() => <LegacyQueryRedirect {...rule} />} />
          ))}
        {legacyParamRedirects.map(({ from, buildTarget }) => (
          <Route
            key={from}
            exact
            path={from}
            render={({ match }) => {
              const entityId = match.params.id || match.params.invoiceId || match.params.deviceId
              if (!entityId) return <Redirect to="/404" />
              return <Redirect to={buildTarget(entityId)} />
            }}
          />
        ))}
        <PrivateRoute exact path={'/expense-categories/:id/edit'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="expenses">
            <EditExpenseCategories />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/user_profile'} currentToken={isAuthenticated} wrapper={React.Fragment}>
          <UserProfile />
        </PrivateRoute>
        <PrivateRoute path={'/products'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="products">
            <ProductsRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={paths.marketing.socialContent} currentToken={isAuthenticated}>
          <SocialMediaAi />
        </PrivateRoute>
        <PrivateRoute exact path={paths.marketing.creativeStudio} currentToken={isAuthenticated}>
          <CreativeStudio />
        </PrivateRoute>
        <PrivateRoute path={'/labors'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="products">
            <LaborsRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/packages'} currentToken={isAuthenticated}>
          <PackagesRoutes />
        </PrivateRoute>
        <PrivateRoute exact path={paths.productBadges.list} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="products">
            <ProductBadgesPage />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/categories'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="products">
            <Categories showSimpleInputTextFilter />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/vendors'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="vendors">
            <VendorsRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={LEGACY_CATALOG_PATHS} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="products">
            <LegacyCatalogRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={paths.payTerms.list} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="products">
            <PayTerms />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={paths.workOrderTypes.list} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="products">
            <WorkOrderTypes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={paths.taxes.list} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="products">
            <Taxes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/customers'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="customers">
            <CustomersRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={['/payments', '/receipt-payments']} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <PaymentsRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/estimates'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <EstimatesRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/warehouses'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="warehouses">
            <WarehousesRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/stock-receipts'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="stock_receipts">
            <StockReceiptsRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/purchase-orders'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="purchase_orders">
            <PurchaseOrdersRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/vendor-bills'} currentToken={isAuthenticated}>
          <ModuleGuardRoute vendorBills>
            <VendorBillsRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/companies'} currentToken={isAuthenticated}>
          <CompaniesRoutes />
        </PrivateRoute>
        <PrivateRoute path={'/users'} currentToken={isAuthenticated}>
          <RoleGuardRoute allow={['admin']}>
            <ModuleGuardRoute module="user_management">
              <UsersRoutes />
            </ModuleGuardRoute>
          </RoleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/user_managment_descontinued'} currentToken={isAuthenticated}>
          <UserManagment />
        </PrivateRoute>
        <PrivateRoute path={'/adjustments'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="inventory_adjustments">
            <AdjustmentsRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/invoices'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <InvoicesRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/work-orders'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <WorkOrdersRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/expenses'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="expenses">
            <ExpensesRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/online-sales'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <OnlineSales />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={paths.saleReturns.list} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <SaleReturns />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path="/sale-returns/:id" currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <SaleReturnDetail />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={paths.storefrontInquiries.list} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <StorefrontInquiriesPage />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path="/storefront-inquiries/:id" currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <StorefrontInquiriesPage />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/recurring-invoices'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <RecurringInvoicesList />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/recurring-invoices/new'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <RecurringInvoiceForm />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/recurring-invoices/:id/edit'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <RecurringInvoiceForm />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/recurring-invoices/:id'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <RecurringInvoiceDetail />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/devices'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <DevicesRoutes />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/work-orders-payments'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <WorkOrderPaymentsPage />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/receive-payments'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <ReceivePaymentsPage />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/receipts'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="sales">
            <Receipt />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/item-inventory'} currentToken={isAuthenticated} wrapper={React.Fragment}>
          <ModuleGuardRoute module="products">
            <ItemInventory />
          </ModuleGuardRoute>
        </PrivateRoute>
        <Route exact path={'/sign_up'}>
          <Signup />
        </Route>
        <Route exact path={'/auth/google/return'}>
          <GoogleReturn />
        </Route>
        <Route exact path={'/auth/google/finish'}>
          <GoogleFinishRegistration />
        </Route>
        <Route exact path={'/validation_code'}>
          <ValidationCode />
        </Route>
        <Route exact path={'/validation_selection'}>
          <ValidationMethodSelection />
        </Route>
        <Route exact path={'/forgot_password'}>
          <ForgetPassword />
        </Route>
        <Route exact path={'/forgetpassword'}>
          <ForgetPassword />
        </Route>
        <Route exact path={paths.account.recover}>
          <RecoverAccountPage />
        </Route>
        <Route exact path={paths.account.cancelDeletion}>
          <CancelDeletionPage />
        </Route>
        <Route exact path={'/dev/lifecycle-preview'}>
          <LifecycleReviewPreview />
        </Route>
        <PrivateRoute exact path={'/stock-transfers'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="stock_transfers">
            <StockTransfersList />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/stock-transfers/new'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="stock_transfers">
            <NewTransfer />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/stock-transfers/:id'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="stock_transfers">
            <TransferDetail />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/admin/companies'} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <SuperAdminCompanies />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/admin/account-deletions'} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <SuperAdminAccountDeletions />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute exact path={paths.admin.salesLeads.list} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <SuperAdminSalesLeads />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/admin/sales-leads/:id'} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <SuperAdminSalesLeads />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute exact path={paths.admin.superAdmins} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <SuperAdminPlatformUsers />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute exact path={paths.admin.superAdminNew} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <SuperAdminPlatformUserForm />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/admin/super-admins/:id/edit'} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <SuperAdminPlatformUserForm />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/admin/companies/:companyId/health'} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <SuperAdminCompanyHealth />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/admin/companies/:companyId/users'} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <SuperAdminCompanyUsers />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/admin/activity'} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <SuperAdminActivity />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/admin/platform-health'} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <SuperAdminHealth />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute exact path={paths.admin.socialContentPrompt} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <SocialContentPrompt />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute exact path={paths.admin.creativeVisionPrompt} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <CreativeVisionPrompt />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute path={paths.admin.pushNotifications} currentToken={isAuthenticated}>
          <SuperAdminRoute currentToken={isAuthenticated}>
            <AdminPushNotifications />
          </SuperAdminRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/profile'} currentToken={isAuthenticated}>
          <ProfileForm />
        </PrivateRoute>
        <PrivateRoute exact path={'/settings/chart-of-accounts'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="settings">
            <ChartOfAccounts />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/settings/subscription'} currentToken={isAuthenticated}>
          <Subscription />
        </PrivateRoute>
        <PrivateRoute exact path={paths.billing.success} currentToken={isAuthenticated}>
          <BillingSuccessPage />
        </PrivateRoute>
        <PrivateRoute exact path={paths.billing.cancel} currentToken={isAuthenticated}>
          <BillingCancelPage />
        </PrivateRoute>
        <PrivateRoute exact path={paths.billing.portalReturn} currentToken={isAuthenticated}>
          <BillingPortalReturnPage />
        </PrivateRoute>
        <PrivateRoute exact path={paths.billing.paypalReturn} currentToken={isAuthenticated}>
          <PaypalReturnPage />
        </PrivateRoute>
        <PrivateRoute exact path={paths.billing.paypalCancel} currentToken={isAuthenticated}>
          <PaypalCancelPage />
        </PrivateRoute>
        <PrivateRoute exact path={paths.settings.legalPolicies} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="settings">
            <StorefrontLegalPage />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/settings/account'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="settings">
            <AccountSettings />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute path={'/settings'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="settings">
            <Settings />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/accounting/overview'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="reports">
            <AccountingOverview />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/accounting/profit-loss'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="reports">
            <ProfitLossReport />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/accounting/accounts-receivable'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="reports">
            <AccountsReceivableReport />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/accounting/sales-report'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="reports">
            <SalesReport />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/accounting/payments-report'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="reports">
            <PaymentsReport />
          </ModuleGuardRoute>
        </PrivateRoute>
        <PrivateRoute exact path={'/accounting/expenses-report'} currentToken={isAuthenticated}>
          <ModuleGuardRoute module="reports">
            <ExpensesReport />
          </ModuleGuardRoute>
        </PrivateRoute>
        <Redirect exact from="/reports" to="/accounting/overview" />
        <Route path={'/view-invoice/:code?'}>
          <ErrorBoundary>
            <SearchInvoice />
          </ErrorBoundary>
        </Route>
        <PrivateRoute exact wrapper={React.Fragment} path={paths.accessDenied} currentToken={isAuthenticated}>
          <AccessDenied />
        </PrivateRoute>
        <Route path="*">
          <PageNotFound />
        </Route>
      </Switch>
    </Suspense>
  )
}

export default Routes
